/**
 * @file formRuleEngine.ts
 * @description Platform intelligence — configurable business rule engine for forms.
 *
 * Rules are evaluated at 3 phases: ON_LOAD, ON_CHANGE, ON_SUBMIT.
 * Each rule has conditions (ALL/ANY) and actions (show/hide/set/block/trigger/alert).
 *
 * This is the runtime evaluator — consumed by DynamicFormPage at each phase.
 */

import type { FieldValue } from '../components/workspace/dynamicFormFieldUtils';

// ═══════════════════════════════════════════════════════════════════════════
//  TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type RuleOperator =
  | 'equals'
  | 'not_equals'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'contains'
  | 'is_empty'
  | 'is_not_empty';

export interface RuleCondition {
  id: string;             // unique condition identifier
  field: string;          // internalName of source field
  operator: RuleOperator;
  value?: string;         // comparison value (JSON-encoded for complex)
}

export type RuleActionType =
  | 'SHOW_FIELD'
  | 'HIDE_FIELD'
  | 'ENABLE_FIELD'
  | 'DISABLE_FIELD'
  | 'SHOW_SECTION'
  | 'HIDE_SECTION'
  | 'SHOW_TAB'
  | 'HIDE_TAB'
  | 'SHOW_COLUMN'
  | 'HIDE_COLUMN'
  | 'SHOW_ZONE'
  | 'HIDE_ZONE'
  | 'SET_VALUE'
  | 'SET_READONLY'
  | 'SET_REQUIRED'
  | 'BLOCK_SUBMIT'
  | 'TRIGGER_WORKFLOW'
  | 'ASSIGN_ROLE'
  | 'RAISE_ALERT'
  | 'CREATE_TASK'
  | 'SEND_NOTIFICATION'
  | 'MOVE_TO_STEP';

export type RuleAction =
  | { type: 'SHOW_FIELD'; target: string }
  | { type: 'HIDE_FIELD'; target: string }
  | { type: 'ENABLE_FIELD'; target: string }
  | { type: 'DISABLE_FIELD'; target: string }
  | { type: 'SHOW_SECTION'; target: string }
  | { type: 'HIDE_SECTION'; target: string }
  | { type: 'SHOW_TAB'; target: string }
  | { type: 'HIDE_TAB'; target: string }
  | { type: 'SHOW_COLUMN'; target: string }
  | { type: 'HIDE_COLUMN'; target: string }
  | { type: 'SHOW_ZONE'; target: string }
  | { type: 'HIDE_ZONE'; target: string }
  | { type: 'SET_VALUE'; target: string; value: string }
  | { type: 'SET_READONLY'; target: string; value: boolean }
  | { type: 'SET_REQUIRED'; target: string; value: boolean }
  | { type: 'BLOCK_SUBMIT'; message: string }
  | { type: 'TRIGGER_WORKFLOW'; workflowCode: string }
  | { type: 'ASSIGN_ROLE'; roleCode: string }
  | { type: 'RAISE_ALERT'; alertCode: string; message?: string }
  | { type: 'CREATE_TASK'; taskType: string; assignToRole?: string; assignToUserField?: string }
  | { type: 'SEND_NOTIFICATION'; notificationCode: string }
  | { type: 'MOVE_TO_STEP'; stepCode: string };

export type RulePhase = 'ON_LOAD' | 'ON_CHANGE' | 'ON_SUBMIT';

export interface FormRule {
  id: string;
  name: string;
  description?: string;
  conditionType: 'ALL' | 'ANY';
  conditions: RuleCondition[];
  actions: RuleAction[];
  phase: RulePhase;
  enabled: boolean;
  priority?: number;       // lower = evaluated first
}

// ═══════════════════════════════════════════════════════════════════════════
//  EVALUATION RESULT
// ═══════════════════════════════════════════════════════════════════════════

export interface RuleEvaluationResult {
  /** Fields to force-show (overrides conditional logic) */
  showFields: Set<string>;
  /** Fields to force-hide */
  hideFields: Set<string>;
  /** Fields to set specific values */
  setValues: Record<string, string>;
  /** Fields to mark read-only */
  readOnlyFields: Set<string>;
  /** Fields to disable (non-editable) */
  disabledFields: Set<string>;
  /** Fields to enable (editable) */
  enabledFields: Set<string>;
  /** Required overrides — true = force required, false = force optional */
  requiredOverrides: Record<string, boolean>;
  /** Submit blockers — if non-empty, form cannot submit */
  blockMessages: string[];
  /** Workflow codes to trigger */
  triggeredWorkflows: string[];
  /** Roles to assign */
  assignedRoles: string[];
  /** Alerts to raise */
  alerts: Array<{ code: string; message?: string }>;
  /** Tasks to create */
  tasks: Array<{ taskType: string; assignToRole?: string; assignToUserField?: string }>;
  /** Notification codes to send */
  notifications: string[];
  /** Workflow steps to transition to */
  moveToSteps: string[];
  /** Sections to show */
  showSections: Set<string>;
  /** Sections to hide */
  hideSections: Set<string>;
  /** Tabs to show */
  showTabs: Set<string>;
  /** Tabs to hide */
  hideTabs: Set<string>;
  /** Columns to show */
  showColumns: Set<string>;
  /** Columns to hide */
  hideColumns: Set<string>;
  /** Zones to show */
  showZones: Set<string>;
  /** Zones to hide */
  hideZones: Set<string>;
}

function emptyResult(): RuleEvaluationResult {
  return {
    showFields: new Set(),
    hideFields: new Set(),
    setValues: {},
    readOnlyFields: new Set(),
    disabledFields: new Set(),
    enabledFields: new Set(),
    requiredOverrides: {},
    blockMessages: [],
    triggeredWorkflows: [],
    assignedRoles: [],
    alerts: [],
    tasks: [],
    notifications: [],
    moveToSteps: [],
    showSections: new Set(),
    hideSections: new Set(),
    showTabs: new Set(),
    hideTabs: new Set(),
    showColumns: new Set(),
    hideColumns: new Set(),
    showZones: new Set(),
    hideZones: new Set(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
//  CONDITION EVALUATOR
// ═══════════════════════════════════════════════════════════════════════════

function evaluateCondition(
  condition: RuleCondition,
  formData: Record<string, FieldValue>,
): boolean {
  const raw = formData[condition.field];
  const strVal = raw == null ? '' : String(raw);
  const numVal = Number(raw);
  const cv = condition.value ?? '';

  switch (condition.operator) {
    case 'equals':
      return strVal === cv;
    case 'not_equals':
      return strVal !== cv;
    case 'gt':
      return !isNaN(numVal) && numVal > Number(cv);
    case 'gte':
      return !isNaN(numVal) && numVal >= Number(cv);
    case 'lt':
      return !isNaN(numVal) && numVal < Number(cv);
    case 'lte':
      return !isNaN(numVal) && numVal <= Number(cv);
    case 'in': {
      const list = cv.split(',').map((s) => s.trim());
      if (Array.isArray(raw)) return raw.some((v) => list.includes(String(v)));
      return list.includes(strVal);
    }
    case 'contains':
      if (Array.isArray(raw)) return raw.some((v) => String(v).includes(cv));
      return strVal.includes(cv);
    case 'is_empty':
      return raw == null || strVal.trim() === '' || (Array.isArray(raw) && raw.length === 0);
    case 'is_not_empty':
      return raw != null && strVal.trim() !== '' && !(Array.isArray(raw) && raw.length === 0);
    default:
      return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  RULE EVALUATOR
// ═══════════════════════════════════════════════════════════════════════════

function evaluateRule(
  rule: FormRule,
  formData: Record<string, FieldValue>,
): boolean {
  if (!rule.enabled || rule.conditions.length === 0) return false;

  const results = rule.conditions.map((c) => evaluateCondition(c, formData));
  return rule.conditionType === 'ALL'
    ? results.every(Boolean)
    : results.some(Boolean);
}

/**
 * Evaluate all rules for a given phase.
 * Returns aggregated actions across all matched rules.
 */
export function evaluateRules(
  rules: FormRule[],
  formData: Record<string, FieldValue>,
  phase: RulePhase,
): RuleEvaluationResult {
  const result = emptyResult();

  // Filter to phase and sort by priority
  const phaseRules = rules
    .filter((r) => r.phase === phase && r.enabled)
    .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));

  for (const rule of phaseRules) {
    if (!evaluateRule(rule, formData)) continue;

    for (const action of rule.actions) {
      switch (action.type) {
        case 'SHOW_FIELD':
          result.showFields.add(action.target);
          result.hideFields.delete(action.target);
          break;
        case 'HIDE_FIELD':
          result.hideFields.add(action.target);
          result.showFields.delete(action.target);
          break;
        case 'ENABLE_FIELD':
          result.enabledFields.add(action.target);
          result.disabledFields.delete(action.target);
          break;
        case 'DISABLE_FIELD':
          result.disabledFields.add(action.target);
          result.enabledFields.delete(action.target);
          break;
        case 'SHOW_SECTION':
          result.showSections.add(action.target);
          result.hideSections.delete(action.target);
          break;
        case 'HIDE_SECTION':
          result.hideSections.add(action.target);
          result.showSections.delete(action.target);
          break;
        case 'SHOW_TAB':
          result.showTabs.add(action.target);
          result.hideTabs.delete(action.target);
          break;
        case 'HIDE_TAB':
          result.hideTabs.add(action.target);
          result.showTabs.delete(action.target);
          break;
        case 'SHOW_COLUMN':
          result.showColumns.add(action.target);
          result.hideColumns.delete(action.target);
          break;
        case 'HIDE_COLUMN':
          result.hideColumns.add(action.target);
          result.showColumns.delete(action.target);
          break;
        case 'SHOW_ZONE':
          result.showZones.add(action.target);
          result.hideZones.delete(action.target);
          break;
        case 'HIDE_ZONE':
          result.hideZones.add(action.target);
          result.showZones.delete(action.target);
          break;
        case 'SET_VALUE':
          result.setValues[action.target] = action.value;
          break;
        case 'SET_READONLY':
          if (action.value) result.readOnlyFields.add(action.target);
          break;
        case 'SET_REQUIRED':
          result.requiredOverrides[action.target] = action.value;
          break;
        case 'BLOCK_SUBMIT':
          result.blockMessages.push(action.message);
          break;
        case 'TRIGGER_WORKFLOW':
          result.triggeredWorkflows.push(action.workflowCode);
          break;
        case 'ASSIGN_ROLE':
          result.assignedRoles.push(action.roleCode);
          break;
        case 'RAISE_ALERT':
          result.alerts.push({ code: action.alertCode, message: action.message });
          break;
        case 'CREATE_TASK':
          result.tasks.push({
            taskType: action.taskType,
            assignToRole: action.assignToRole,
            assignToUserField: action.assignToUserField,
          });
          break;
        case 'SEND_NOTIFICATION':
          result.notifications.push(action.notificationCode);
          break;
        case 'MOVE_TO_STEP':
          result.moveToSteps.push(action.stepCode);
          break;
      }
    }
  }

  return result;
}

// ═══════════════════════════════════════════════════════════════════════════
//  VALIDATION (design-time — for builder)
// ═══════════════════════════════════════════════════════════════════════════

export interface RuleValidationError {
  ruleId: string;
  message: string;
}

/**
 * Validate rules at design time — checks field references, action integrity.
 */
export function validateRules(
  rules: FormRule[],
  validFieldNames: Set<string>,
): RuleValidationError[] {
  const errors: RuleValidationError[] = [];

  for (const rule of rules) {
    if (!rule.name?.trim()) {
      errors.push({ ruleId: rule.id, message: 'Rule must have a name' });
    }
    if (rule.conditions.length === 0) {
      errors.push({ ruleId: rule.id, message: `Rule "${rule.name}" has no conditions` });
    }
    if (rule.actions.length === 0) {
      errors.push({ ruleId: rule.id, message: `Rule "${rule.name}" has no actions` });
    }

    // Validate field references in conditions
    for (const cond of rule.conditions) {
      if (!validFieldNames.has(cond.field)) {
        errors.push({
          ruleId: rule.id,
          message: `Rule "${rule.name}" references unknown field "${cond.field}"`,
        });
      }
    }

    // Validate field references in actions
    for (const action of rule.actions) {
      if ('target' in action && action.target && !validFieldNames.has(action.target)) {
        errors.push({
          ruleId: rule.id,
          message: `Rule "${rule.name}" targets unknown field "${action.target}"`,
        });
      }
    }

    // Validate automation action references (codes must not be empty)
    for (const action of rule.actions) {
      switch (action.type) {
        case 'TRIGGER_WORKFLOW':
          if (!action.workflowCode?.trim())
            errors.push({ ruleId: rule.id, message: `Rule "${rule.name}": TRIGGER_WORKFLOW requires a workflow code` });
          break;
        case 'ASSIGN_ROLE':
          if (!action.roleCode?.trim())
            errors.push({ ruleId: rule.id, message: `Rule "${rule.name}": ASSIGN_ROLE requires a role code` });
          break;
        case 'RAISE_ALERT':
          if (!action.alertCode?.trim())
            errors.push({ ruleId: rule.id, message: `Rule "${rule.name}": RAISE_ALERT requires an alert code` });
          break;
        case 'CREATE_TASK':
          if (!action.taskType?.trim())
            errors.push({ ruleId: rule.id, message: `Rule "${rule.name}": CREATE_TASK requires a task type` });
          if (!action.assignToRole?.trim() && !action.assignToUserField?.trim())
            errors.push({ ruleId: rule.id, message: `Rule "${rule.name}": CREATE_TASK needs assignToRole or assignToUserField` });
          break;
        case 'SEND_NOTIFICATION':
          if (!action.notificationCode?.trim())
            errors.push({ ruleId: rule.id, message: `Rule "${rule.name}": SEND_NOTIFICATION requires a notification code` });
          break;
        case 'MOVE_TO_STEP':
          if (!action.stepCode?.trim())
            errors.push({ ruleId: rule.id, message: `Rule "${rule.name}": MOVE_TO_STEP requires a step code` });
          break;
      }
    }

    // Detect conflicting actions within same rule
    const hasBlockSubmit = rule.actions.some((a) => a.type === 'BLOCK_SUBMIT');
    const hasMoveToStep = rule.actions.some((a) => a.type === 'MOVE_TO_STEP');
    if (hasBlockSubmit && hasMoveToStep) {
      errors.push({
        ruleId: rule.id,
        message: `Rule "${rule.name}": BLOCK_SUBMIT and MOVE_TO_STEP cannot coexist — submit is blocked so step cannot move`,
      });
    }

    // Detect multiple conflicting MOVE_TO_STEP targets
    const moveSteps = rule.actions.filter((a) => a.type === 'MOVE_TO_STEP') as Array<Extract<RuleAction, { type: 'MOVE_TO_STEP' }>>;
    if (moveSteps.length > 1) {
      const uniqueSteps = new Set(moveSteps.map((a) => a.stepCode));
      if (uniqueSteps.size > 1) {
        errors.push({
          ruleId: rule.id,
          message: `Rule "${rule.name}": multiple conflicting MOVE_TO_STEP targets`,
        });
      }
    }
  }

  return errors;
}

// ═══════════════════════════════════════════════════════════════════════════
//  OPERATOR METADATA (for builder UI)
// ═══════════════════════════════════════════════════════════════════════════

export const RULE_OPERATORS: Array<{ value: RuleOperator; label: string; requiresValue: boolean }> = [
  { value: 'equals', label: 'Equals', requiresValue: true },
  { value: 'not_equals', label: 'Not Equals', requiresValue: true },
  { value: 'gt', label: 'Greater Than', requiresValue: true },
  { value: 'gte', label: 'Greater or Equal', requiresValue: true },
  { value: 'lt', label: 'Less Than', requiresValue: true },
  { value: 'lte', label: 'Less or Equal', requiresValue: true },
  { value: 'in', label: 'In List', requiresValue: true },
  { value: 'contains', label: 'Contains', requiresValue: true },
  { value: 'is_empty', label: 'Is Empty', requiresValue: false },
  { value: 'is_not_empty', label: 'Is Not Empty', requiresValue: false },
];

export const RULE_ACTION_TYPES: Array<{ value: RuleActionType; label: string; phase: RulePhase[] }> = [
  { value: 'SHOW_FIELD', label: 'Show Field', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'HIDE_FIELD', label: 'Hide Field', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'ENABLE_FIELD', label: 'Enable Field', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'DISABLE_FIELD', label: 'Disable Field', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'SHOW_SECTION', label: 'Show Section', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'HIDE_SECTION', label: 'Hide Section', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'SHOW_TAB', label: 'Show Tab', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'HIDE_TAB', label: 'Hide Tab', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'SHOW_COLUMN', label: 'Show Column', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'HIDE_COLUMN', label: 'Hide Column', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'SHOW_ZONE', label: 'Show Zone', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'HIDE_ZONE', label: 'Hide Zone', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'SET_VALUE', label: 'Set Value', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'SET_READONLY', label: 'Set Read-Only', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'SET_REQUIRED', label: 'Set Required', phase: ['ON_LOAD', 'ON_CHANGE'] },
  { value: 'BLOCK_SUBMIT', label: 'Block Submit', phase: ['ON_SUBMIT'] },
  { value: 'TRIGGER_WORKFLOW', label: 'Trigger Workflow', phase: ['ON_SUBMIT'] },
  { value: 'ASSIGN_ROLE', label: 'Assign Role', phase: ['ON_SUBMIT'] },
  { value: 'RAISE_ALERT', label: 'Raise Alert', phase: ['ON_CHANGE', 'ON_SUBMIT'] },
  { value: 'CREATE_TASK', label: 'Create Task', phase: ['ON_SUBMIT'] },
  { value: 'SEND_NOTIFICATION', label: 'Send Notification', phase: ['ON_SUBMIT'] },
  { value: 'MOVE_TO_STEP', label: 'Move to Step', phase: ['ON_SUBMIT'] },
];

export const RULE_PHASES: Array<{ value: RulePhase; label: string; description: string }> = [
  { value: 'ON_LOAD', label: 'On Load', description: 'Evaluated when form first renders' },
  { value: 'ON_CHANGE', label: 'On Change', description: 'Evaluated after any field value changes' },
  { value: 'ON_SUBMIT', label: 'On Submit', description: 'Evaluated before form submission' },
];

// ═══════════════════════════════════════════════════════════════════════════
//  ADVANCED VALIDATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Detect self-referential SET_VALUE loops within a single rule.
 * (A rule that reads field X in conditions and sets field X in SET_VALUE actions.)
 */
export function detectSelfReferentialRules(rules: FormRule[]): RuleValidationError[] {
  const errors: RuleValidationError[] = [];
  for (const rule of rules) {
    const conditionFields = new Set(rule.conditions.map((c) => c.field));
    for (const action of rule.actions) {
      if (action.type === 'SET_VALUE' && conditionFields.has(action.target)) {
        errors.push({
          ruleId: rule.id,
          message: `Rule "${rule.name}" sets "${action.target}" which it also reads — potential infinite loop`,
        });
      }
    }
  }
  return errors;
}

/**
 * Detect circular dependencies between rules.
 * Rule A reads field X, sets field Y. Rule B reads field Y, sets field X.
 */
export function detectCircularDependencies(rules: FormRule[]): RuleValidationError[] {
  const errors: RuleValidationError[] = [];
  // Build graph: ruleId -> { reads: Set<field>, writes: Set<field> }
  const graph = rules.map((r) => ({
    id: r.id,
    name: r.name,
    reads: new Set(r.conditions.map((c) => c.field)),
    writes: new Set(
      r.actions
        .filter((a): a is Extract<typeof a, { target: string }> => 'target' in a && a.type === 'SET_VALUE')
        .map((a) => a.target),
    ),
  }));

  for (let i = 0; i < graph.length; i++) {
    for (let j = i + 1; j < graph.length; j++) {
      const a = graph[i];
      const b = graph[j];
      // A writes something B reads AND B writes something A reads
      const aWritesBReads = [...a.writes].some((f) => b.reads.has(f));
      const bWritesAReads = [...b.writes].some((f) => a.reads.has(f));
      if (aWritesBReads && bWritesAReads) {
        errors.push({
          ruleId: a.id,
          message: `Circular dependency between "${a.name}" and "${b.name}"`,
        });
      }
    }
  }
  return errors;
}

/**
 * Full design-time validation including circular dependency checks.
 */
export function validateRulesFull(
  rules: FormRule[],
  validFieldNames: Set<string>,
): RuleValidationError[] {
  return [
    ...validateRules(rules, validFieldNames),
    ...detectSelfReferentialRules(rules),
    ...detectCircularDependencies(rules),
  ];
}

// ═══════════════════════════════════════════════════════════════════════════
//  RULE DEPENDENCY ANALYSIS (for builder badges)
// ═══════════════════════════════════════════════════════════════════════════

/** Get how many rules reference a given field (as condition or action target). */
export function getRuleCountForField(rules: FormRule[], fieldInternalName: string): number {
  return rules.filter((r) =>
    r.conditions.some((c) => c.field === fieldInternalName) ||
    r.actions.some((a) => 'target' in a && a.target === fieldInternalName),
  ).length;
}

/** Get fields affected by a given field's value (through rule actions). */
export function getAffectedFieldsByRules(rules: FormRule[], fieldInternalName: string): string[] {
  const affected = new Set<string>();
  for (const rule of rules) {
    if (rule.conditions.some((c) => c.field === fieldInternalName)) {
      for (const action of rule.actions) {
        if ('target' in action && action.target) {
          affected.add(action.target);
        }
      }
    }
  }
  return [...affected];
}
